import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, BookOpen, Target, Users, Award, Lightbulb, Heart, Infinity, ArrowLeft } from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';
import GrainTexture from '@/components/GrainTexture';
import logoSluitertijd from '@/assets/logosluitertijd2.png';
import logoSluitertijdWebp from '@/assets/logosluitertijd2.webp';
import onderstroomImage from '@/assets/onderstroom.jpg';
import onderstroomImageWebp from '@/assets/onderstroom.webp';
import systemiqueLogo from '@/assets/systemique.png';
import systemiqueLogoWebp from '@/assets/systemique.webp';
import heropaars from '@/assets/heropaars.png';
import heropaarsWebp from '@/assets/heropaars.webp';
import opleidingenBridge from '@/assets/webp bestanden/193 - TwentyTwelvePhotography_Systemique.webp?w=1600&format=webp';
import opleidingenBridgeSrcset from '@/assets/webp bestanden/193 - TwentyTwelvePhotography_Systemique.webp?w=480;800;1200;1600&format=webp&as=srcset';
import LeergangOnderstroomDialog from '@/components/LeergangOnderstroomDialog';
import MaatwerkOpleidingDialog from '@/components/MaatwerkOpleidingDialog';
import { SEO } from '@/components/SEO';

const Opleidingstrajecten = () => {
  const navigate = useNavigate();
  const [leergangDialogOpen, setLeergangDialogOpen] = useState(false);
  const [maatwerkDialogOpen, setMaatwerkDialogOpen] = useState(false);

  return (
    <>
      <SEO 
        title="Opleidingen systemisch werk Amsterdam & Noord-Holland | Systemique"
        description="Overzicht van opleidingen en trainingen systemisch werk in Amsterdam, Haarlem en Noord-Holland. Maatwerk trajecten voor teams en organisaties, plus het Sluitertijd-programma. Bekijk hier het volledige aanbod."
        keywords="opleidingen systemisch werk Amsterdam, opleidingen systemisch werken, systemische trainingen, maatwerk opleiding systemisch werk, systemische opleiding Haarlem, training systemisch Noord-Holland, systemische opleiding Castricum, opleidingstrajecten organisaties, sluitertijd traject, training systemisch Zaanstreek"
        canonical="https://systemique.nl/opleidingstrajecten"
      />
      
      {/* Custom Luxury Header */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            Opleidings<span className="italic font-light font-serif text-[#F9EBEF]">trajecten</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Ontwikkel jezelf, je team of organisatie verder met systemische trainingen.
          </p>
        </div>
      </section>

      {/* WAAROM-BLOK */}
      <section className="py-12 md:py-20 bg-white relative px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <Badge className="mb-4 md:mb-6">Waarom systemische opleidingen?</Badge>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl text-[#6b4912] mb-6 md:mb-8 leading-tight px-2">
            Ontwikkel jezelf, je team of organisatie verder
          </h2>
          <div className="space-y-4 text-base sm:text-lg md:text-xl text-[#6b4912] leading-relaxed font-light px-2">
            <p>
              Systemisch werk biedt een unieke manier om naar organisaties, teams en individuen te kijken. Door de onderstroom zichtbaar te maken, krijg je inzicht in patronen en dynamieken die anders onzichtbaar blijven.
            </p>
            <p>
              Onze opleidingstrajecten zijn praktijkgericht en direct toepasbaar. Je leert niet alleen de theorie, maar vooral hoe je systemisch denken en handelen kunt integreren in je dagelijkse werk.
            </p>
          </div>
          <div className="h-1 w-24 bg-[#854C73]/20 mx-auto rounded-full mt-8 md:mt-12"></div>
        </div>
      </section>

      {/* BRIDGE IMAGE - straddles white (WAAROM) and pink (AANBOD) sections */}
      <div className="relative">
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1/2 bg-white"></div>
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2 bg-[#FAEBEE]"></div>
        <div className="container mx-auto max-w-5xl px-4 sm:px-6 relative z-10 py-8 md:py-12">
          <div className="group rounded-3xl overflow-hidden shadow-2xl">
            <OptimizedImage
              src={opleidingenBridge}
              srcSet={opleidingenBridgeSrcset}
              sizes="(max-width: 1024px) 100vw, 1024px"
              alt="Deelnemers tijdens een opleiding systemisch werk bij Systemique — Amsterdam en Noord-Holland"
              className="w-full h-[280px] md:h-[420px] object-cover object-center transition-transform duration-500 ease-out group-hover:scale-110"
              width={1600}
              height={600}
            />
          </div>
        </div>
      </div>

      {/* AANBOD GRID (3 Kaarten) */}
      <section id="aanbod" className="py-12 md:py-24 bg-[#FAEBEE] relative overflow-hidden px-4 sm:px-6">
        <GrainTexture opacity={0.15} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <Badge className="mb-4">Aanbod</Badge>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#6b4912]">
              Opleidingen
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {/* Kaart 1: Leergang systemisch leiderschap */}
            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-6 md:p-8 flex flex-col h-full">
                <div className="relative w-16 h-16 mb-6 mx-auto">
                  <div className="absolute inset-0 rounded-full bg-[#FAEBEE] flex items-center justify-center overflow-hidden">
                    <OptimizedImage 
                      src={onderstroomImage} 
                      webpSrc={onderstroomImageWebp}
                      alt="Leergang systemisch leiderschap - Duiken in de Onderstroom bij Systemique"
                      className="absolute inset-0 w-full h-full object-cover rounded-full z-0"
                      width={64}
                      height={64}
                    />
                    <div className="absolute inset-0 rounded-full bg-[#854C73] opacity-20 z-10"></div>
                    <OptimizedImage 
                      src={systemiqueLogo} 
                      webpSrc={systemiqueLogoWebp}
                      alt="Systemique - Leergang systemisch leiderschap"
                      className="absolute inset-0 w-full h-full object-contain rounded-full z-20 p-2"
                      width={64}
                      height={64}
                    />
                  </div>
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-normal mb-1 text-center text-[#854C73] flex flex-col items-center justify-center">
                  Leergang systemisch leiderschap
                </h3>
                <p className="font-serif text-sm md:text-base italic text-center text-[#854C73]/80 mb-4">
                  Duiken in de Onderstroom
                </p>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#6b4912]">Diepgaand inzicht in systeemdynamieken</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#854C73]">Praktijkgericht met real life cases</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#6b4912]">Kleine groep voor persoonlijke aandacht</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#854C73]">Compacte leergang (3 dagen)</span>
                  </li>
                </ul>
                <Button 
                  variant="outline"
                  className="w-full border-[#854C73] text-[#854C73] hover:bg-[#854C73]/10"
                  onClick={() => navigate('/leergang-systemisch-leiderschap')}
                >
                  Bekijk leergang systemisch leiderschap
                </Button>
              </CardContent>
            </Card>

            {/* Kaart 2: Systemisch werken in organisaties */}
            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-6 md:p-8 flex flex-col h-full">
                <div className="relative w-16 h-16 mb-6 mx-auto">
                  <div className="absolute inset-0 rounded-full bg-[#FAEBEE] flex items-center justify-center overflow-hidden">
                    <OptimizedImage 
                      src={systemiqueLogo} 
                      webpSrc={systemiqueLogoWebp}
                      alt="Systemique - Systemisch werken in organisaties"
                      className="absolute inset-0 w-full h-full object-contain rounded-full z-10 p-2"
                      width={64}
                      height={64}
                    />
                    <Users className="absolute inset-0 w-full h-full p-3 text-[#854C73] z-20" size={64} />
                  </div>
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-normal mb-4 text-center text-[#854C73] min-h-[3rem] md:min-h-[3.5rem] flex items-center justify-center">
                  Systemisch werken in organisaties
                </h3>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#854C73]">Op maat gemaakt voor jouw organisatie</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#6b4912]">Praktijkgerichte trainingen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#854C73]">Afgestemd op specifieke behoeften</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#6b4912]">Voor teams en organisaties</span>
                  </li>
                </ul>
                <Button 
                  variant="outline"
                  className="w-full border-[#854C73] text-[#854C73] hover:bg-[#854C73]/10"
                  onClick={() => setMaatwerkDialogOpen(true)}
                >
                  Vraag offerte aan
                </Button>
              </CardContent>
            </Card>

            {/* Kaart 3: Sluitertijd */}
            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow">
              <CardContent className="p-6 md:p-8 flex flex-col h-full">
                <div className="relative w-16 h-16 mb-6 mx-auto">
                  <div className="absolute inset-0 rounded-full bg-[#FAEBEE] flex items-center justify-center overflow-hidden">
                    <OptimizedImage 
                      src={systemiqueLogo} 
                      webpSrc={systemiqueLogoWebp}
                      alt="Systemique - Sluitertijd opleidingstraject"
                      className="absolute inset-0 w-full h-full object-contain rounded-full z-10 p-2"
                      width={64}
                      height={64}
                    />
                    <OptimizedImage 
                      src={logoSluitertijd} 
                      webpSrc={logoSluitertijdWebp}
                      alt="Sluitertijd logo - Systemische opleidingstrajecten bij Systemique"
                      className="absolute inset-0 w-full h-full object-contain rounded-full z-20 scale-[3] translate-y-3"
                      width={64}
                      height={64}
                    />
                  </div>
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-normal mb-4 text-center text-[#854C73] min-h-[3rem] md:min-h-[3.5rem] flex items-center justify-center">
                  Sluitertijd
                </h3>
                <ul className="space-y-3 mb-8 flex-1">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#6b4912]">Ontwikkelprogramma voor professionals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#854C73]">Richting geven aan werk en toekomst</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#6b4912]">Scherper zicht op jouw roeping</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#854C73] shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base text-[#854C73]">Voor organisaties en individuen</span>
                  </li>
                </ul>
                <Button 
                  variant="outline"
                  className="w-full border-[#854C73] text-[#854C73] hover:bg-[#854C73]/10"
                  onClick={() => window.open('https://sluitertijd.com', '_blank')}
                >
                  Bezoek Sluitertijd programma
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* VOORDELEN GRID (6 Items) */}
      <section className="py-12 md:py-24 bg-white px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <Badge className="mb-4">Voordelen</Badge>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#6b4912]">
              Wat levert een systemische opleiding <span className="italic font-light font-serif text-[#854C73]">jou op</span>?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              { 
                icon: Target, 
                title: "Diepgaand Inzicht", 
                desc: "Ontdek waar patronen vandaan komen en wat de oplossing is." 
              },
              { 
                icon: Users, 
                title: "Betere Samenwerking", 
                desc: "Versterk de dynamiek in je team door de onderstroom te begrijpen." 
              },
              { 
                icon: Award, 
                title: "Praktische Vaardigheden", 
                desc: "Leer systemisch kijken en handelen in je dagelijkse werk." 
              },
              { 
                icon: Lightbulb, 
                title: "Nieuwe Perspectieven", 
                desc: "Krijg een andere bril op organisaties, teams en individuen." 
              },
              { 
                icon: Heart, 
                title: "Persoonlijke Groei", 
                desc: "Ontwikkel jezelf systemisch en krijg meer zelfinzicht." 
              },
              { 
                icon: Infinity, 
                title: "Duurzame Verandering", 
                desc: "Creëer ruimte voor blijvende transformatie in je organisatie." 
              }
            ].map((item, i) => {
              // Variatie in kleuren: oneven items krijgen #6b4912, even items krijgen #854C73
              const titleColor = i % 2 === 0 ? '#6b4912' : '#854C73';
              const descColor = i % 2 === 0 ? '#854C73' : '#6b4912';
              
              return (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 bg-[#FAEBEE] rounded-full flex items-center justify-center mx-auto mb-6 text-[#854C73]">
                    <item.icon size={32} />
                  </div>
                  <h3 className="font-serif text-xl font-bold mb-3" style={{ color: titleColor }}>
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: descColor }}>
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: '#efe8dc' }}>
        <GrainTexture opacity={0.15} />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="max-w-3xl mx-auto animate-fade-up">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" style={{ color: '#6b4912' }}>
              Klaar om je systemische vaardigheden te ontwikkelen?
            </h2>
            <p className="text-lg mb-8 leading-relaxed" style={{ color: '#6b4912' }}>
              Laten we kennismaken en ontdekken hoe systemisch werk jouw organisatie of team kan ondersteunen.
            </p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <Button asChild variant="white" size="lg" className="bg-white" style={{ color: '#854C73' }}>
                <Link to="/contact">Plan een gesprek</Link>
              </Button>
              <Button asChild variant="outline" size="lg" style={{ borderColor: '#854C73', color: '#6b4912' }} className="hover:bg-[#854C73]/10">
                <Link to="/aanbod">Alle systemisch werk diensten</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Dialogs */}
      <LeergangOnderstroomDialog open={leergangDialogOpen} onOpenChange={setLeergangDialogOpen} />
      <MaatwerkOpleidingDialog open={maatwerkDialogOpen} onOpenChange={setMaatwerkDialogOpen} />
    </>
  );
};

export default Opleidingstrajecten;
